import umlCanvasEngine from './UmlCanvasEngine.js';
import umlCanvasSaver from './UmlCanvasSaver.js';
import { OpenToast } from '../../JxControls/JxToasts/StyledToast.jsx';

/**
 * Saves the currently loaded UmlSchema note — the SINGLE save implementation,
 * used by every save entry point in UmlWorkspace: the canvas toolbar's Save
 * button, the S keyboard shortcut (UmlCanvas.useConnectorKeyboardShortcuts.js),
 * AND the UmlSchemaConfigDlg Save button on every tab (Properties/Import/
 * Files/Body, see UmlSchemaConfigDlg.handleSave). Previously the dialog had
 * its own separate, drifted copy of this logic (consolidated 2026-07-14) —
 * that duplication is exactly why the viewport-persistence fix only landed on
 * one of the two paths at first. One implementation now; a future fix here
 * automatically covers every save button.
 *
 * @param {{rk?: string|null, name?: string, service?: string, format?: string, remarks?: string}} [overrides]
 *   Identity fields to use INSTEAD of umlCanvasEngine.loadedSchemaInfo for this
 *   save. The toolbar/S-key path calls this with no args (pure engine state —
 *   there's no dialog form to read from). UmlSchemaConfigDlg.handleSave passes
 *   its own local `this.state.inputs` here instead, since the Properties tab's
 *   Name/Service/Format/Remarks fields may hold edits not yet reflected in
 *   umlCanvasEngine.loadedSchemaInfo (which only ever tracks rk/name/remarks,
 *   never service/format — hence overrides, not a full replacement).
 * @returns {Promise<{saved: object, rk: string}|null>} null on failure
 */
export async function saveCurrentSchema(overrides = {}) {
    try {
        const identity = { ...umlCanvasEngine.loadedSchemaInfo, ...overrides };
        if (!identity.name?.trim()) {
            OpenToast('Invalid name for Schema', 'error');
            return null;
        }
        const schemaBody = umlCanvasEngine.toSaveSchema();
        const saved = await umlCanvasSaver.saveSchema(identity.rk, schemaBody, identity);
        const resolvedRk = saved && !identity.rk && saved.rk ? saved.rk : identity.rk;
        umlCanvasEngine.setLoadedSchemaInfo({ rk: resolvedRk, name: identity.name, remarks: identity.remarks });
        umlCanvasEngine.markSaved();
        OpenToast(`UmlSchema: "${identity.name || saved?.title || 'Untitled'}" saved`, undefined, { className: 'jx-toast-accent' });
        return { saved, rk: resolvedRk };
    } catch (error) {
        umlCanvasSaver.logError('saveCurrentSchema', error.message);
        return null;
    }
}
